import { useState } from 'react';
import {
  Button,
  DataTable,
  FilterBar,
  FilterField,
  Input,
  KpiCard,
  KpiGrid,
  PageShell,
  Pagination,
  RadixSelect,
  Skeleton,
  StatusBadge,
  type DataTableColumn,
} from '@/components/ui';
import { useHasRole } from '@/providers/AuthProvider';
import { labelOf, DEPOSIT_WITHDRAWAL_STATUS_LABELS } from '@/lib/enumLabels';
import { UserSearchSelect } from '@/features/users/components/UserSearchSelect';
import {
  useDepositBalanceQuery,
  useDepositLedgerQuery,
  useDepositWithdrawalsQuery,
  useAdminDepositMutation,
} from '../hooks/useAdminDepositQuery';
import { ManualAdjustDialog, type AdjustDirection } from '../components/ManualAdjustDialog';
import { WithdrawalActions } from '../components/WithdrawalActions';
import { MILEAGE_WRITE_ROLES } from '../lib/mileageActionRbac';
import type { DepositLedger, DepositWithdrawal, DepositWithdrawalStatus } from '../types';

const PAGE_SIZE = 20;
const ALL = '';

const STATUS_OPTIONS = [
  { value: ALL, label: '전체 상태' },
  { value: 'REQUESTED', label: '신청' },
  { value: 'APPROVED', label: '승인' },
  { value: 'PAID', label: '지급완료' },
  { value: 'REJECTED', label: '반려' },
];

const STATUS_VARIANT: Record<DepositWithdrawalStatus, 'info' | 'warning' | 'success' | 'danger'> = {
  REQUESTED: 'info',
  APPROVED: 'warning',
  PAID: 'success',
  REJECTED: 'danger',
};

const formatDateTime = (iso: string | null): string => (iso ? new Date(iso).toLocaleString('ko-KR') : '-');
const formatSigned = (n: number): string => `${n > 0 ? '+' : ''}${n.toLocaleString()}`;

export const DepositAdminPage = () => {
  const canWrite = useHasRole(MILEAGE_WRITE_ROLES);

  const [userId, setUserId] = useState<string | null>(null);

  const [draftKind, setDraftKind] = useState('');
  const [draftFrom, setDraftFrom] = useState('');
  const [draftTo, setDraftTo] = useState('');
  const [ledgerApplied, setLedgerApplied] = useState({ kind: '', from: '', to: '', page: 1 });

  const [withdrawalStatus, setWithdrawalStatus] = useState(ALL);
  const [withdrawalPage, setWithdrawalPage] = useState(1);

  const [manualDir, setManualDir] = useState<AdjustDirection | null>(null);

  const balanceQuery = useDepositBalanceQuery(userId);
  const ledgerQuery = useDepositLedgerQuery(
    {
      ...(userId ? { userId } : {}),
      ...(ledgerApplied.kind ? { kind: ledgerApplied.kind } : {}),
      ...(ledgerApplied.from ? { occurredFrom: ledgerApplied.from } : {}),
      ...(ledgerApplied.to ? { occurredTo: ledgerApplied.to } : {}),
      page: ledgerApplied.page,
      size: PAGE_SIZE,
    },
    Boolean(userId),
  );
  const withdrawalsQuery = useDepositWithdrawalsQuery({
    ...(withdrawalStatus ? { status: withdrawalStatus as DepositWithdrawalStatus } : {}),
    page: withdrawalPage,
    size: PAGE_SIZE,
  });
  const mutation = useAdminDepositMutation();

  const applyLedger = () =>
    setLedgerApplied({ kind: draftKind, from: draftFrom, to: draftTo, page: 1 });

  const ledgerColumns: DataTableColumn<DepositLedger>[] = [
    {
      key: 'occurredAt',
      header: '일시',
      width: 170,
      cell: (row) => (
        <span className="text-sm tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {formatDateTime(row.occurredAt)}
        </span>
      ),
    },
    {
      key: 'kind',
      header: '종류',
      width: 120,
      cell: (row) => (
        <span className="text-sm" style={{ color: 'var(--content-muted)' }}>
          {row.kind || '—'}
        </span>
      ),
    },
    {
      key: 'amount',
      header: '금액',
      align: 'right',
      width: 120,
      cell: (row) => (
        <span
          className="text-sm font-medium tabular-nums"
          style={{ color: row.amount < 0 ? 'var(--status-danger-fg)' : 'var(--content-strong)' }}
        >
          {formatSigned(row.amount)}
        </span>
      ),
    },
    {
      key: 'balanceAfter',
      header: '거래 후 잔액',
      align: 'right',
      width: 120,
      cell: (row) => (
        <span className="text-sm tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {row.balanceAfter.toLocaleString()}
        </span>
      ),
    },
    {
      key: 'title',
      header: '설명',
      cell: (row) => (
        <span className="block max-w-[260px] truncate text-sm" style={{ color: 'var(--content-muted)' }} title={row.title ?? ''}>
          {row.title || '-'}
        </span>
      ),
    },
  ];

  const withdrawalColumns: DataTableColumn<DepositWithdrawal>[] = [
    {
      key: 'requestedAt',
      header: '신청일시',
      width: 170,
      cell: (row) => (
        <span className="text-sm tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {formatDateTime(row.requestedAt)}
        </span>
      ),
    },
    {
      key: 'amount',
      header: '출금액',
      align: 'right',
      width: 120,
      cell: (row) => (
        <span className="text-sm font-medium tabular-nums" style={{ color: 'var(--content-strong)' }}>
          {row.amount.toLocaleString()}
        </span>
      ),
    },
    {
      key: 'status',
      header: '상태',
      align: 'center',
      width: 120,
      cell: (row) => (
        <StatusBadge variant={STATUS_VARIANT[row.status] ?? 'neutral'} label={labelOf(DEPOSIT_WITHDRAWAL_STATUS_LABELS, row.status)} />
      ),
    },
    {
      key: 'processedAt',
      header: '처리일시',
      width: 170,
      cell: (row) => (
        <span className="text-sm tabular-nums" style={{ color: 'var(--content-muted)' }}>
          {formatDateTime(row.processedAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: 80,
      cell: (row) => (
        <WithdrawalActions
          withdrawal={row}
          canWrite={canWrite}
          busy={
            mutation.approveWithdrawal.isPending ||
            mutation.payWithdrawal.isPending ||
            mutation.rejectWithdrawal.isPending
          }
          onApprove={(id) => mutation.approveWithdrawal.mutate(id)}
          onPay={(id) => mutation.payWithdrawal.mutate(id)}
          onReject={(input) => mutation.rejectWithdrawal.mutate(input)}
        />
      ),
    },
  ];

  const ledgerPagination = ledgerQuery.data?.pagination;
  const withdrawalsPagination = withdrawalsQuery.data?.pagination;
  const manualBusy = mutation.grant.isPending || mutation.deduct.isPending;

  return (
    <PageShell title="예치금 관리" description="회원 예치금 잔액·원장을 조회하고 지급/차감하며, 출금 신청을 승인·지급·반려합니다.">
      <FilterBar
        className="mb-4"
        filters={
          <FilterField label="회원" className="col-span-full">
            {/* QA-420 — 필터 그리드 한 칸(약 200px)에 갇혀 검색창이 좁았다 — 한 줄을 다 쓰고 480px 로 고정. */}
            <div className="w-[480px] max-w-[calc(100vw-4rem)]">
            <UserSearchSelect
              value={userId}
              onChange={(id) => {
                setUserId(id);
                setLedgerApplied((prev) => ({ ...prev, page: 1 }));
              }}
              testId="deposit-user-select"
              ariaLabel="회원 검색"
            />
            </div>
          </FilterField>
        }
        actions={
          canWrite ? (
            <>
              <Button size="sm" disabled={!userId} onClick={() => setManualDir('grant')}>
                지급
              </Button>
              <Button size="sm" variant="danger" disabled={!userId} onClick={() => setManualDir('deduct')}>
                차감
              </Button>
            </>
          ) : undefined
        }
      />

      {userId ? (
        <KpiGrid columns={3} className="mb-4">
          <KpiCard
            density="compact"
            label="현재 예치금 잔액"
            value={balanceQuery.isPending ? '...' : `${(balanceQuery.data?.balance ?? 0).toLocaleString()}원`}
          />
        </KpiGrid>
      ) : (
        <p className="mb-4 rounded-md p-4 text-sm" style={{ background: 'var(--surface-1)', color: 'var(--content-muted)' }}>
          회원을 선택하면 잔액과 원장 내역이 표시됩니다.
        </p>
      )}

      {userId ? (
        <>
          <FilterBar
            className="mb-3"
            filters={
              <>
                <FilterField label="종류">
                  <Input value={draftKind} onChange={(e) => setDraftKind(e.target.value)} aria-label="종류" placeholder="CHARGE / USE / REFUND ..." />
                </FilterField>
                <FilterField label="시작일">
                  <Input type="date" value={draftFrom} onChange={(e) => setDraftFrom(e.target.value)} aria-label="시작일" />
                </FilterField>
                <FilterField label="종료일">
                  <Input type="date" value={draftTo} onChange={(e) => setDraftTo(e.target.value)} aria-label="종료일" />
                </FilterField>
              </>
            }
            actions={
              <Button size="sm" onClick={applyLedger}>
                조회
              </Button>
            }
          />

          {ledgerQuery.isPending ? (
            <div role="status" className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-[40px] w-full" />
              ))}
            </div>
          ) : (
            <DataTable
              columns={ledgerColumns}
              rows={[...(ledgerQuery.data?.items ?? [])]}
              rowKey={(row) => row.id}
              emptyState="원장 내역이 없습니다."
              stickyHeader
            />
          )}

          {ledgerPagination ? (
            <Pagination
              total={ledgerPagination.totalItems}
              currentPage={ledgerPagination.page}
              pageSize={PAGE_SIZE}
              onPageChange={(p) => setLedgerApplied((prev) => ({ ...prev, page: p }))}
              className="mt-3"
            />
          ) : null}
        </>
      ) : null}

      <div className="mt-8">
        <h3 className="mb-3 text-[15px] font-semibold" style={{ color: 'var(--content-strong)' }}>
          출금 신청
        </h3>
        <FilterBar
          className="mb-3"
          filters={
            <FilterField label="상태">
              <RadixSelect
                value={withdrawalStatus}
                onChange={(v) => {
                  setWithdrawalStatus(v);
                  setWithdrawalPage(1);
                }}
                options={STATUS_OPTIONS}
                aria-label="출금 상태"
              />
            </FilterField>
          }
        />
        {withdrawalsQuery.isPending ? (
          <div role="status" className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-[40px] w-full" />
            ))}
          </div>
        ) : (
          <DataTable
            columns={withdrawalColumns}
            rows={[...(withdrawalsQuery.data?.items ?? [])]}
            rowKey={(row) => row.id}
            emptyState="출금 신청이 없습니다."
            stickyHeader
          />
        )}
        {withdrawalsPagination ? (
          <Pagination
            total={withdrawalsPagination.totalItems}
            currentPage={withdrawalsPagination.page}
            pageSize={PAGE_SIZE}
            onPageChange={setWithdrawalPage}
            className="mt-3"
          />
        ) : null}
      </div>

      {manualDir && userId ? (
        <ManualAdjustDialog
          open
          onOpenChange={(o) => { if (!o) setManualDir(null); }}
          direction={manualDir}
          variant="deposit"
          busy={manualBusy}
          memberLabel="선택된 회원"
          onSubmit={({ amount, reason }) => {
            const payload = { userId, amount, title: reason };
            const m = manualDir === 'grant' ? mutation.grant : mutation.deduct;
            m.mutate(payload, { onSuccess: () => setManualDir(null) });
          }}
        />
      ) : null}
    </PageShell>
  );
};
